import { useState } from "react";
import type { PlanEvent, Task } from "../../../shared/types";
import type { HolidayEntry } from "../../../shared/hooks/use-holidays";
import { getToday } from "../../../shared/types/date-helpers";

type MiniCalendarVariant = "default" | "google";

interface MiniCalendarProps {
  readonly events: PlanEvent[];
  readonly tasks: Task[];
  readonly referenceDate: string;
  readonly onSelectDate: (date: string) => void;
  readonly variant?: MiniCalendarVariant;
  readonly holidays?: ReadonlyMap<string, HolidayEntry>;
}

const MONTH_NAMES = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

export function MiniCalendar({
  events,
  tasks,
  referenceDate,
  onSelectDate,
  variant = "default",
  holidays,
}: MiniCalendarProps) {
  const today = getToday();
  const refDate = new Date(`${referenceDate}T00:00:00`);
  const [monthOffset, setMonthOffset] = useState(0);
  const [prevRef, setPrevRef] = useState(referenceDate);

  // Reset offset when referenceDate changes (derived state from props)
  if (referenceDate !== prevRef) {
    setMonthOffset(0);
    setPrevRef(referenceDate);
  }

  const targetDate = new Date(refDate.getFullYear(), refDate.getMonth() + monthOffset, 1);
  const year = targetDate.getFullYear();
  const month = targetDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

  const daysWithEvents = (() => {
    const dates = new Set<string>();
    const yearStr = `${year}-${String(month + 1).padStart(2, "0")}`;
    for (const e of events) {
      if (e.date.substring(0, 7) === yearStr) dates.add(e.date);
    }
    for (const t of tasks) {
      if (t.dueDate && t.dueDate.substring(0, 7) === yearStr) dates.add(t.dueDate);
    }
    return dates;
  })();

  const weeks: (number | null)[][] = [];
  let week: (number | null)[] = new Array(startingDayOfWeek).fill(null);
  for (let day = 1; day <= daysInMonth; day++) {
    week.push(day);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  const weekDayLabels = ["L", "M", "M", "J", "V", "S", "D"];

  const goToPrevMonth = () => setMonthOffset((o) => o - 1);
  const goToNextMonth = () => setMonthOffset((o) => o + 1);

  // Style Google Calendar
  if (variant === "google") {
    return (
      <div className="px-2">
        {/* Month navigation header */}
        <div className="flex items-center justify-between mb-2 px-1">
          <span className="text-sm font-medium text-ink">
            {MONTH_NAMES[month]} {year}
          </span>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={goToPrevMonth}
              aria-label="Mois précédent"
              className="w-6 h-6 flex items-center justify-center rounded-full text-ink-muted hover:bg-surface-alt active:bg-surface-alt/70 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={goToNextMonth}
              aria-label="Mois suivant"
              className="w-6 h-6 flex items-center justify-center rounded-full text-ink-muted hover:bg-surface-alt active:bg-surface-alt/70 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-0">
          {weekDayLabels.map((label, i) => (
            <div
              key={`day-${i}`}
              className="text-center text-[10px] font-medium text-ink-faint py-1"
            >
              {label}
            </div>
          ))}

          {weeks.flatMap((w, weekIdx) =>
            w.map((day, dayIdx) => {
              if (day === null) {
                return (
                  <div
                    key={`empty-${weekIdx}-${dayIdx}`}
                    className="aspect-square"
                  />
                );
              }

              const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const isToday = dateStr === today;
              const isSelected = dateStr === referenceDate && !isToday;
              const hasEventOrTask = daysWithEvents.has(dateStr);
              const hEntry = holidays?.get(dateStr);

              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => onSelectDate(dateStr)}
                  title={hEntry ? (hEntry.official ? `${hEntry.name} — Jour férié` : hEntry.name) : undefined}
                  className={`aspect-square flex flex-col items-center justify-center rounded-full text-xs transition-colors relative
                    ${isToday ? "bg-accent text-white font-medium" : ""}
                    ${isSelected ? "bg-accent/15 text-accent font-medium" : ""}
                    ${!isToday && !isSelected && hEntry ? hEntry.official ? "text-red-500 dark:text-red-400 font-medium" : "text-ink-faint font-medium" : ""}
                    ${!isToday && !isSelected && !hEntry ? "text-ink hover:bg-surface-alt active:bg-surface-alt/70" : ""}
                  `}
                >
                  <span>{day}</span>
                  {hasEventOrTask && !isToday && (
                    <div className="absolute bottom-0.5 w-1 h-1 rounded-full bg-accent" />
                  )}
                  {hasEventOrTask && isToday && (
                    <div className="absolute bottom-0.5 w-1 h-1 rounded-full bg-white" />
                  )}
                </button>
              );
            }),
          )}
        </div>
      </div>
    );
  }

  // Style par défaut
  return (
    <div className="px-4 py-4 bg-surface-alt rounded-xl">
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-7 gap-1">
          {weekDayLabels.map((label, i) => (
            <div
              key={`day-${i}`}
              className="text-center text-[10px] font-semibold text-ink-muted py-1"
            >
              {label}
            </div>
          ))}

          {weeks.map((w, weekIdx) => {
            const isCurrentWeek = w.some((day) => {
              if (day === null) return false;
              const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              return dateStr === today;
            });

            return (
              <div
                key={`week-${weekIdx}`}
                className={`grid grid-cols-7 gap-1 col-span-7 rounded-lg ${
                  isCurrentWeek ? "border border-dashed border-accent/50 p-0.5 -m-0.5" : ""
                }`}
              >
                {w.map((day, dayIdx) => {
                  if (day === null) {
                    return (
                      <div
                        key={`empty-${weekIdx}-${dayIdx}`}
                        className="aspect-square flex items-center justify-center text-xs"
                      />
                    );
                  }

                  const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const isToday = dateStr === today;
                  const isSelected = dateStr === referenceDate && !isToday;
                  const hasEventOrTask = daysWithEvents.has(dateStr);
                  const hEntry = holidays?.get(dateStr);

                  return (
                    <button
                      key={dateStr}
                      type="button"
                      onClick={() => onSelectDate(dateStr)}
                      title={hEntry ? (hEntry.official ? `${hEntry.name} — Jour férié` : hEntry.name) : undefined}
                      className={`aspect-square flex flex-col items-center justify-center rounded text-xs font-medium transition-colors relative
                        ${isToday ? "bg-accent text-white" : isSelected ? "border border-dashed border-accent text-accent" : !hEntry ? "hover:bg-surface active:bg-surface/70" : hEntry.official ? "text-red-500 dark:text-red-400 hover:bg-surface active:bg-surface/70" : "text-ink-faint hover:bg-surface active:bg-surface/70"}
                      `}
                    >
                      <span>{day}</span>
                      {hasEventOrTask && (
                        <div className={`absolute bottom-0.5 w-1 h-1 rounded-full ${isToday ? "bg-white" : "bg-accent"}`} />
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
